import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";

import AdminLayout from "@/components/admin/AdminLayout";
import { AdminFilterBar } from "@/components/admin/AdminSurface";
import SubscribersTable from "@/components/admin/subscription/SubscribersTable";
import SubscriptionForm from "@/components/admin/subscription/SubscriptionForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/auth";
import { useAdminSubscriptions } from "@/hooks/useAdminSubscriptions";
import { extendAdminSubscription } from "@/services/subscriptionApi";
import { ProfileData } from "@/types/supabase";
import { Subscriber } from "@/types/subscription";
import { debugLogger } from "@/utils/debug";

type SubscriptionFilter = "all" | "admin" | "stripe" | "attention";

const filterLabels: Record<SubscriptionFilter, string> = {
  all: "All subscribers",
  admin: "Admin grants",
  stripe: "Stripe managed",
  attention: "Needs attention",
};

export default function SubscriptionManagementPage() {
  const [filter, setFilter] = useState<SubscriptionFilter>("attention");
  const [searchQuery, setSearchQuery] = useState("");
  const [prefillUser, setPrefillUser] = useState<ProfileData | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: allSubscriptions = [], isLoading, refetch } = useAdminSubscriptions();
  const { isAdmin } = useAuth();

  const buildPrefillUser = (subscriber: Subscriber): ProfileData | null => {
    if (subscriber.profile) {
      return {
        ...subscriber.profile,
        email: subscriber.email,
      };
    }

    if (!subscriber.user_id) {
      return null;
    }

    return {
      id: subscriber.user_id,
      first_name: '',
      last_name: '',
      email: subscriber.email,
      avatar_url: null,
      mfa_enabled: null,
      created_at: subscriber.created_at,
      whatsapp_number: null,
      whatsapp_verified: false,
    };
  };

  const filteredSubscriptions = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return allSubscriptions
      .filter((subscription) => {
        const matchesFilter =
          filter === "all" ||
          (filter === "admin" && subscription.subscription_source === "admin") ||
          (filter === "stripe" && subscription.subscription_source === "stripe") ||
          (filter === "attention" && ["grace_period", "requires_action", "past_due", "canceled"].includes(subscription.status || ""));

        if (!matchesFilter) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const haystack = [
          subscription.email,
          subscription.profile?.first_name,
          subscription.profile?.last_name,
          subscription.profile?.whatsapp_number,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(normalizedSearch);
      })
      .sort((left, right) => {
        const leftDate = new Date(left.created_at).getTime();
        const rightDate = new Date(right.created_at).getTime();
        return rightDate - leftDate;
      });
  }, [allSubscriptions, filter, searchQuery]);

  const counts = useMemo(
    () => ({
      all: allSubscriptions.length,
      admin: allSubscriptions.filter((subscription) => subscription.subscription_source === "admin").length,
      stripe: allSubscriptions.filter((subscription) => subscription.subscription_source === "stripe").length,
      attention: allSubscriptions.filter((subscription) => ["grace_period", "requires_action", "past_due", "canceled"].includes(subscription.status || "")).length,
    }),
    [allSubscriptions],
  );

  const handleSubscriptionChange = async (id: string, updates: Partial<Subscriber>) => {
    if (!isAdmin) {
      toast({
        title: "Not authorized",
        description: "You are not authorized to perform this action.",
        variant: "destructive",
      });
      return;
    }

    try {
      const currentSubscription = allSubscriptions.find((subscription) => subscription.id === id);
      if (!currentSubscription?.user_id || !updates.expires_at) {
        throw new Error('Only explicit admin-grant extensions are supported from this view.');
      }

      if (currentSubscription.subscription_source !== 'admin') {
        throw new Error('Stripe-managed subscriptions must be managed through Stripe, not from the admin dashboard.');
      }

      await extendAdminSubscription({
        targetUserId: currentSubscription.user_id,
        expiresAt: updates.expires_at,
        adminNotes: updates.admin_notes || null,
      });

      toast({
        title: "Admin grant extended",
        description: "The admin-managed subscription expiry has been updated.",
      });

      await queryClient.invalidateQueries({ queryKey: ["adminSubscriptions"] });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to update subscription.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const refetchSubscriptions = () => {
    void queryClient.invalidateQueries({ queryKey: ["adminSubscriptions"] });
    void refetch();
  };

  debugLogger.log("Subscriptions count:", allSubscriptions?.length);

  return (
    <AdminLayout
      title="Subscriptions"
      description="Admin grants and Stripe-managed subscriptions are tracked together here. Stripe billing remains read-only in the app."
      actions={
        <SubscriptionForm
          onSubscriptionAdded={refetchSubscriptions}
          prefillUser={prefillUser}
          onPrefillHandled={() => setPrefillUser(null)}
        />
      }
    >
      <div className="space-y-6">
        <AdminFilterBar>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="w-full max-w-md">
              <label className="mb-2 block text-sm font-medium text-gray-700">Search subscribers</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or WhatsApp"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="h-11 rounded-lg border-gray-200 bg-white pl-9"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {(Object.keys(filterLabels) as SubscriptionFilter[]).map((filterKey) => (
                <Button
                  key={filterKey}
                  type="button"
                  variant={filter === filterKey ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(filterKey)}
                  className={
                    filter === filterKey
                      ? "rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark"
                      : "rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
                    }
                >
                  {filterLabels[filterKey]}
                  <span className="ml-1 rounded-full bg-black/10 px-2 py-0.5 text-[11px] font-semibold text-current">
                    {counts[filterKey]}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
            <p>
              Showing {filteredSubscriptions.length} of {allSubscriptions.length} subscribers.
            </p>
            {(searchQuery || filter !== "attention") && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 justify-start rounded-lg px-0 text-gray-600 hover:bg-transparent hover:text-whatsapp"
                onClick={() => {
                  setSearchQuery("");
                  setFilter("attention");
                }}
              >
                Reset to needs attention
              </Button>
            )}
          </div>
        </AdminFilterBar>

        <SubscribersTable
          data={filteredSubscriptions}
          loading={isLoading}
          onSubscriptionChange={handleSubscriptionChange}
          onGrantAdminSubscription={(subscriber) => setPrefillUser(buildPrefillUser(subscriber))}
          summary={`Focus stays on ${filterLabels[filter].toLowerCase()} so common support actions take fewer steps.`}
        />
      </div>
    </AdminLayout>
  );
}
